import * as PopoverPrimitive from "@radix-ui/react-popover";
import { AnimatePresence, m } from "framer-motion";
import { CalendarDays, Check, ChevronDown } from "lucide-react";
import { useEffect, useState } from "react";
import { cn } from "../cn";
import {
  endOfDay,
  parseISODate,
  startOfDay,
  subCalendarDays,
  toISODate,
} from "../day";
import { formatLongDate } from "../format";
import { springSoft, useMotionInitial, useMotionTransition } from "../motion";
import { Combobox, type ComboboxOption } from "./Combobox";
import { DatePicker } from "./DatePicker";
import { useSheetPortalContainer } from "./Sheet";
import { Switch } from "./Switch";

/** An inclusive calendar-day range, both bounds as `YYYY-MM-DD`. */
export interface DateRangeValue {
  readonly start: string;
  readonly end: string;
}

export type DateRangePreset =
  | "last30Days"
  | "thisMonth"
  | "previousMonth"
  | "thisYear"
  | "custom";

export type DateRangeComparisonMode =
  | "previousPeriod"
  | "previousYear"
  | "custom";

/**
 * The full state of a report period picker: which preset is active, the
 * range it resolves to, and (optionally) what it's being compared against.
 * `range`/`comparisonRange` are always kept resolved rather than derived by
 * the consumer, so a view-configuration-driven report can read them straight
 * off the selection without re-implementing the preset math.
 */
export interface DateRangeSelection {
  readonly preset: DateRangePreset;
  readonly range: DateRangeValue;
  readonly compare: boolean;
  readonly comparisonMode: DateRangeComparisonMode;
  readonly comparisonRange?: DateRangeValue;
  /** Only meaningful when `preset`/`comparisonMode` is `"custom"`. */
  readonly customRange?: DateRangeValue;
  readonly customComparisonRange?: DateRangeValue;
}

const presetOptions: { id: DateRangePreset; label: string }[] = [
  { id: "last30Days", label: "Afgelopen 30 dagen" },
  { id: "thisMonth", label: "Deze maand" },
  { id: "previousMonth", label: "Vorige maand" },
  { id: "thisYear", label: "Dit jaar" },
  { id: "custom", label: "Aangepaste periode" },
];

const COMPARISON_MODE_OPTIONS: readonly ComboboxOption[] = [
  { value: "previousPeriod", label: "Vorige periode" },
  { value: "previousYear", label: "Dezelfde periode vorig jaar" },
  { value: "custom", label: "Aangepaste periode" },
];

/** Resolves a preset (other than `"custom"`) to a concrete range, anchored on `anchor` (defaults to today). */
export function resolvePresetRange(
  preset: Exclude<DateRangePreset, "custom">,
  anchor: Date = new Date(),
): DateRangeValue {
  const end = endOfDay(anchor);

  if (preset === "thisMonth") {
    return {
      start: toISODate(new Date(anchor.getFullYear(), anchor.getMonth(), 1)),
      end: toISODate(end),
    };
  }
  if (preset === "previousMonth") {
    const start = new Date(anchor.getFullYear(), anchor.getMonth() - 1, 1);
    const previousEnd = new Date(anchor.getFullYear(), anchor.getMonth(), 0);
    return { start: toISODate(start), end: toISODate(previousEnd) };
  }
  if (preset === "thisYear") {
    return {
      start: toISODate(new Date(anchor.getFullYear(), 0, 1)),
      end: toISODate(end),
    };
  }
  // last30Days
  const start = new Date(end);
  start.setDate(start.getDate() - 29);
  return { start: toISODate(startOfDay(start)), end: toISODate(end) };
}

/**
 * Resolves a selection's active range. A committed selection already carries a
 * resolved `range`, so that is preferred over re-deriving one: a persisted or
 * view-configuration-driven `"custom"` selection may omit the optional
 * `customRange` duplicate, and silently substituting a preset there would show
 * a different period from the one the report actually queried.
 */
export function resolveDateRange(
  selection: Pick<DateRangeSelection, "preset" | "customRange"> &
    Partial<Pick<DateRangeSelection, "range">>,
  anchor: Date = new Date(),
): DateRangeValue {
  if (selection.preset === "custom") {
    return (
      selection.customRange ??
      selection.range ??
      resolvePresetRange("last30Days", anchor)
    );
  }
  return resolvePresetRange(selection.preset, anchor);
}

function daysBetween(range: DateRangeValue): number {
  const start = parseISODate(range.start);
  const end = parseISODate(range.end);
  if (!start || !end) return 0;
  return Math.round((end.getTime() - start.getTime()) / 86_400_000) + 1;
}

/**
 * Same calendar day one year earlier, clamped to the end of the target month.
 * Without the clamp `2024-02-29` would roll forward into `2023-03-01`, moving a
 * "same period last year" comparison into the wrong month.
 */
function sameDayLastYear(date: Date): Date {
  const year = date.getFullYear() - 1;
  const month = date.getMonth();
  const lastDayOfMonth = new Date(year, month + 1, 0).getDate();
  return new Date(year, month, Math.min(date.getDate(), lastDayOfMonth));
}

/** Resolves the comparison range for an (already resolved) active range. */
export function resolveComparisonRange(
  range: DateRangeValue,
  selection: Pick<
    DateRangeSelection,
    "comparisonMode" | "customComparisonRange"
  >,
): DateRangeValue {
  if (
    selection.comparisonMode === "custom" &&
    selection.customComparisonRange
  ) {
    return selection.customComparisonRange;
  }
  if (selection.comparisonMode === "previousYear") {
    const start = parseISODate(range.start);
    const end = parseISODate(range.end);
    if (start && end) {
      return {
        start: toISODate(sameDayLastYear(start)),
        end: toISODate(sameDayLastYear(end)),
      };
    }
  }
  // previousPeriod: an equally-long window directly before the active range.
  const start = parseISODate(range.start);
  const length = daysBetween(range) || 1;
  if (!start) return range;
  const previousEnd = new Date(start);
  previousEnd.setDate(previousEnd.getDate() - 1);
  const previousStart = new Date(previousEnd);
  previousStart.setDate(previousStart.getDate() - (length - 1));
  return { start: toISODate(previousStart), end: toISODate(previousEnd) };
}

function formatRange(range: DateRangeValue): string {
  const start = parseISODate(range.start);
  const end = parseISODate(range.end);
  if (!start || !end) return "";
  return `${formatLongDate(start)} - ${formatLongDate(end)}`;
}

export interface DateRangePickerProps {
  readonly value: DateRangeSelection;
  readonly onValueChange: (value: DateRangeSelection) => void;
  readonly "aria-label": string;
  /** "Today" for preset resolution. Defaults to `new Date()`; override to anchor on the latest known data instead of the wall clock. */
  readonly anchor?: Date;
  /**
   * Longest custom (or custom comparison) span selectable, in inclusive
   * days. Omitted callers get an unbounded picker; pass this when the
   * consuming report has its own backend-enforced cap, so the picker cannot
   * commit a range that endpoint would reject.
   */
  readonly maxRangeDays?: number;
  readonly className?: string;
}

/**
 * Report-period picker: a trigger summarising the active (and, if enabled,
 * comparison) range, opening a popover with period presets, a custom
 * start/end pair (built on {@link DatePicker}), and an optional comparison
 * period. Mirrors the `PeriodControl` prototyped in the design lab's Ledger
 * reports module, rebuilt as a stateless, view-configuration-friendly
 * primitive: callers own `DateRangeSelection` and every field in it is
 * already resolved, so nothing here needs to be re-derived downstream.
 */
export function DateRangePicker({
  value,
  onValueChange,
  "aria-label": ariaLabel,
  anchor = new Date(),
  maxRangeDays,
  className,
}: DateRangePickerProps) {
  const popoverTransition = useMotionTransition(springSoft);
  const popoverInitial = useMotionInitial({ opacity: 0, scale: 0.96, y: 4 });
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState(value);
  const sheetContainer = useSheetPortalContainer();

  useEffect(() => {
    if (open) setDraft(value);
  }, [open, value]);

  // The committed selection carries its own resolved ranges, so the trigger
  // reads them rather than re-deriving from the preset: re-deriving would drift
  // away from the period the report actually queried once the anchor moves.
  const range = value.range;
  const comparisonRange = value.compare
    ? (value.comparisonRange ?? resolveComparisonRange(range, value))
    : null;

  const draftRange = resolveDateRange(draft, anchor);
  const draftComparisonRange = resolveComparisonRange(draftRange, draft);
  // Neither the active period nor its comparison can reach into days that
  // have not happened yet: the endpoints behind a report have no data for
  // them, and a comparison against a shorter, still-elapsing period would
  // silently compare unequal window lengths.
  const maxDate = toISODate(anchor);

  const triggerLabel = comparisonRange
    ? `${ariaLabel}: ${formatRange(range)}, vergeleken met ${formatRange(comparisonRange)}`
    : `${ariaLabel}: ${formatRange(range)}`;

  return (
    <PopoverPrimitive.Root open={open} onOpenChange={setOpen}>
      <PopoverPrimitive.Trigger asChild>
        <button
          type="button"
          aria-label={triggerLabel}
          aria-haspopup="dialog"
          aria-expanded={open}
          className={cn(
            "inline-flex h-10 items-center gap-2 rounded-xl border border-input bg-surface px-3 text-sm font-medium shadow-sm transition-colors hover:bg-muted/50 focus-visible:border-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/15",
            className,
          )}
        >
          <CalendarDays
            className="size-4 text-muted-foreground"
            aria-hidden="true"
          />
          <span>{formatRange(range)}</span>
          {value.compare && comparisonRange && (
            <span className="hidden border-l border-border pl-2 text-xs font-normal text-muted-foreground sm:inline">
              vergeleken met {formatRange(comparisonRange)}
            </span>
          )}
          <ChevronDown
            aria-hidden="true"
            className={cn(
              "size-4 text-muted-foreground transition-transform",
              open && "rotate-180",
            )}
          />
        </button>
      </PopoverPrimitive.Trigger>

      <AnimatePresence>
        {open && (
          <PopoverPrimitive.Portal
            forceMount
            container={sheetContainer ?? undefined}
          >
            <PopoverPrimitive.Content
              asChild
              forceMount
              align="end"
              sideOffset={4}
            >
              <m.div
                initial={popoverInitial}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.97, y: 2 }}
                transition={popoverTransition}
                role="dialog"
                aria-label="Periode instellen"
                className="z-50 w-[min(34rem,calc(100vw-2rem))] rounded-xl border border-border bg-popover p-4 text-popover-foreground shadow-lg focus:outline-none"
              >
                <div className="grid gap-4 sm:grid-cols-[11rem_minmax(0,1fr)]">
                  <div className="space-y-1">
                    {presetOptions.map((preset) => (
                      <button
                        key={preset.id}
                        type="button"
                        onClick={() =>
                          setDraft((current) => ({
                            ...current,
                            preset: preset.id,
                            customRange:
                              preset.id === "custom"
                                ? (current.customRange ?? draftRange)
                                : current.customRange,
                          }))
                        }
                        className={cn(
                          "flex h-9 w-full items-center justify-between rounded-lg px-2.5 text-left text-sm transition-colors",
                          draft.preset === preset.id
                            ? "bg-muted font-medium text-foreground"
                            : "text-muted-foreground hover:bg-muted/50 hover:text-foreground",
                        )}
                      >
                        {preset.label}
                        {draft.preset === preset.id && (
                          <Check className="size-4" aria-hidden="true" />
                        )}
                      </button>
                    ))}
                  </div>

                  <div className="space-y-4 border-t border-border/70 pt-4 sm:border-l sm:border-t-0 sm:pl-4 sm:pt-0">
                    {/* Not disabled for non-custom presets: editing a date is
                        how the user switches to "Aangepaste periode" — the
                        onChange already sets `preset: "custom"` on the draft. */}
                    <DateRangeInputs
                      range={draftRange}
                      maxDate={maxDate}
                      maxRangeDays={maxRangeDays}
                      onChange={(customRange) =>
                        setDraft((current) => ({
                          ...current,
                          preset: "custom",
                          customRange,
                        }))
                      }
                    />

                    <div className="flex items-center justify-between gap-2 text-sm font-medium">
                      <span>Vergelijken met</span>
                      <Switch
                        size="sm"
                        aria-label="Vergelijken met een andere periode"
                        checked={draft.compare}
                        onCheckedChange={(compare) =>
                          setDraft((current) => ({ ...current, compare }))
                        }
                      />
                    </div>

                    {draft.compare && (
                      <div className="space-y-3">
                        <Combobox
                          size="sm"
                          aria-label="Vergelijkingsperiode"
                          value={draft.comparisonMode}
                          onValueChange={(value) =>
                            setDraft((current) => ({
                              ...current,
                              comparisonMode: value as DateRangeComparisonMode,
                            }))
                          }
                          options={COMPARISON_MODE_OPTIONS}
                          searchable={false}
                          clearable={false}
                          fluid
                        />
                        {draft.comparisonMode === "custom" ? (
                          <DateRangeInputs
                            range={draftComparisonRange}
                            startLabel="Startdatum vergelijkingsperiode"
                            endLabel="Einddatum vergelijkingsperiode"
                            maxDate={maxDate}
                            maxRangeDays={maxRangeDays}
                            onChange={(customComparisonRange) =>
                              setDraft((current) => ({
                                ...current,
                                customComparisonRange,
                              }))
                            }
                          />
                        ) : (
                          <p className="text-xs text-muted-foreground">
                            {formatRange(draftComparisonRange)}
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                </div>

                <div className="mt-4 flex justify-end gap-2 border-t border-border/70 pt-3">
                  <button
                    type="button"
                    onClick={() => setOpen(false)}
                    className="h-9 rounded-lg px-3 text-sm font-medium text-muted-foreground hover:bg-muted hover:text-foreground"
                  >
                    Annuleren
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      onValueChange({
                        ...draft,
                        range: draftRange,
                        comparisonRange: draft.compare
                          ? draftComparisonRange
                          : undefined,
                      });
                      setOpen(false);
                    }}
                    className="h-9 rounded-lg bg-primary px-3 text-sm font-medium text-primary-foreground hover:bg-primary/90"
                  >
                    Toepassen
                  </button>
                </div>
              </m.div>
            </PopoverPrimitive.Content>
          </PopoverPrimitive.Portal>
        )}
      </AnimatePresence>
    </PopoverPrimitive.Root>
  );
}

function DateRangeInputs({
  range,
  onChange,
  startLabel = "Startdatum",
  endLabel = "Einddatum",
  maxDate,
  maxRangeDays,
}: {
  range: DateRangeValue;
  onChange: (range: DateRangeValue) => void;
  /**
   * The visible `Van`/`Tot en met` captions are not associated with the
   * controls, so when two pairs are on screen at once each needs its own
   * accessible name to say which period it edits.
   */
  startLabel?: string;
  endLabel?: string;
  /** Latest selectable day for both fields (inclusive), e.g. "today". */
  maxDate?: string;
  /** See {@link DateRangePickerProps.maxRangeDays}. */
  maxRangeDays?: number;
}) {
  const startDate = parseISODate(range.start);
  const endDate = parseISODate(range.end);
  // The earliest start that still keeps the span within `maxRangeDays`,
  // counting both boundaries as a day (mirroring `daysBetween`) — `undefined`
  // when uncapped or `range.end` fails to parse (never in practice; a
  // `DateRangeValue` is always well-formed).
  const spanFloor =
    maxRangeDays !== undefined && endDate
      ? toISODate(subCalendarDays(endDate, maxRangeDays - 1))
      : undefined;
  // The latest end for the same reason, mirrored: `subCalendarDays` with a
  // negative count adds days rather than subtracting them.
  const spanCeiling =
    maxRangeDays !== undefined && startDate
      ? toISODate(subCalendarDays(startDate, -(maxRangeDays - 1)))
      : undefined;

  // The start field's own cap is the tightest of: it must never sit after
  // the end field, never after `maxDate`, and never so far before the end
  // field that the span would exceed `maxRangeDays`. ISO days are
  // zero-padded, so lexicographic comparison is chronological.
  const startMax =
    maxDate !== undefined && maxDate < range.end ? maxDate : range.end;
  const startMin = spanFloor;
  // The end field mirrors the start field: never before it, never after
  // `maxDate`, and never so far after it that the span would exceed
  // `maxRangeDays`.
  const endMax =
    spanCeiling !== undefined &&
    (maxDate === undefined || spanCeiling < maxDate)
      ? spanCeiling
      : maxDate;

  return (
    <div className="grid grid-cols-2 gap-2">
      <div>
        <span className="text-xs text-muted-foreground">Van</span>
        <DatePicker
          compact
          aria-label={startLabel}
          value={range.start}
          clearable={false}
          min={startMin}
          max={startMax}
          className="mt-1"
          onValueChange={(start) =>
            onChange({ start, end: start > range.end ? start : range.end })
          }
        />
      </div>
      <div>
        <span className="text-xs text-muted-foreground">Tot en met</span>
        <DatePicker
          compact
          aria-label={endLabel}
          value={range.end}
          clearable={false}
          min={range.start}
          max={endMax}
          className="mt-1"
          onValueChange={(end) =>
            onChange({ start: end < range.start ? end : range.start, end })
          }
        />
      </div>
    </div>
  );
}
